import Chord from "@/module/chord";
import chordTranspose from "@/module/chordTranspose";

const _deshortcutDirective = (directive) => {
	return directive
		.replace(/^soc$/, "start_of_chorus")
		.replace(/^eoc$/, "end_of_chorus")
		.replace(/^sov$/, "start_of_verse")
		.replace(/^eov$/, "end_of_verse")
		.replace(/^sob$/, "start_of_bridge")
		.replace(/^eob$/, "end_of_bridge")
		.replace(/^c$/, "comment")
		.replace(/^ci$/, "comment")
		.replace(/^cb$/, "comment")
		.replace(/^comment_italic$/, "comment")
		.replace(/^comment_box$/, "comment")
}

const _parseDirectiveName = (name) => {
	return name.replace("start_of_", "").replace("end_of_", "");
}

const _cleanLine = (line) => {
	return line
};

const _parseDirective = (line) => {
	let buff;
	if ((buff = line.match(/{(.*): (.*)}/))) {
		const directive = _deshortcutDirective(buff[1])
		return {
			directive: _parseDirectiveName(directive),
			value: buff[2],
			isStart: /start_/.test(directive),
			isEnd: /end_/.test(directive),
		}
	}
	if ((buff = line.match(/{(.*)}/))) {
		const directive = _deshortcutDirective(buff[1])
		return {
			directive: _parseDirectiveName(directive),
			value: null,
			isStart: /start_/.test(directive),
			isEnd: /end_/.test(directive),
		}
	}

	return null;
}

const _parseBlocks = (chordpro) => {
	let blocks = [];
	let parameters = [];
	let currentBlock = null;
	const lines = chordpro.split("\n").map(row => row.replace(/^\s+|\s+$/g, '') === "" ? "" : row);

	lines.forEach((row, index) => {
		if (row.length === 0) {
			if (currentBlock) {
				blocks.push(currentBlock);
				currentBlock = null;
			}
			return;
		}

		const directive = _parseDirective(row);

		if (currentBlock === null) {
			if (directive && directive.isStart) {
				currentBlock = {
					type: directive.directive,
					value: directive.value,
					content: []
				}
			} else if (directive && !directive.isStart && !directive.isEnd) {
				if (directive.directive === "chorus") {
					currentBlock = {
						type: "reference",
						reference: "chorus",
						value: directive.value
					}
				} else if (directive.directive === "verse") {
					currentBlock = {
						type: "reference",
						reference: "verse",
						value: directive.value
					}
				} else if (directive.directive === "bridge") {
					currentBlock = {
						type: "reference",
						reference: "bridge",
						value: directive.value
					}
				} else if (directive.directive === "comment") {
					currentBlock = {
						type: "comment",
						value: directive.value,
						marginBottom: true
					}
				}
			} else {
				currentBlock = {
					type: "paragraph",
					value: null,
					content: [ row ]
				}
			}
		} else {
			if (directive && directive.isEnd) {
				blocks.push(currentBlock);
				currentBlock = null;
			} else {
				if (currentBlock.content) {
					currentBlock.content.push(row);
				} else {
					currentBlock = {
						type: "paragraph",
						value: null,
						content: [ row ]
					}
				}
			}
		}

		if (currentBlock !== null) {
			if (currentBlock.type === "comment") {
				// Komentář bez odsazení řádky od dalšího obsahu
				const nextRow = lines[index + 1];
				if (nextRow !== undefined && nextRow.length !== 0) {
					currentBlock.marginBottom = false;
					blocks.push(currentBlock);
					currentBlock = null
				}
			}
			if (index === lines.length - 1) {
				// Poslední řádka
				blocks.push(currentBlock);
				currentBlock = null
			}
		}
	})

	return { blocks, parameters };
}

const _parseChars = (line) => {
	let chars = [];
	let currentChar = null;

	[...line].forEach((c, index) => {
		if (currentChar === null) {
			if (c === "[") {
				currentChar = {
					char: "",
					chord: "",
					isChordOpened: true,
					waitingForChar: true
				}
			} else {
				currentChar = {
					char: c,
					isChordOpened: false,
					waitingForChar: false
				}
			}
		} else {
			if (c === "]") {
				currentChar.isChordOpened = false;
			} else if (currentChar && currentChar.isChordOpened) {
				currentChar.chord += c;
			} else if (currentChar && !currentChar.isChordOpened && currentChar.waitingForChar) {
				currentChar.char = c;
				currentChar.waitingForChar = false;
			}
		}

		if ((currentChar && !currentChar.isChordOpened && !currentChar.waitingForChar) || (index === line.length - 1)) {
			delete currentChar.isChordOpened;
			delete currentChar.waitingForChar;
			chars.push(currentChar);
			currentChar = null;
		}
	});

	return chars;
}

const _parseLine = (line) => {
	let chords = [];
	let lyrics = "";
	let prevChord = null;
	_parseChars(line).forEach((c, position) => {
		lyrics += c.char;
		if (c.chord) {
			const offset = prevChord ? (position - prevChord.position - prevChord.chord.length - prevChord.offsetDebt) : position;
			chords.push(prevChord = {
				chord: c.chord,
				position: position,
				offset: offset > 0 ? offset : 0,
				offsetDebt: offset < 0 ? Math.abs(offset) : 0
			})
		}
	})

	return { chords, lyrics: _cleanLine(lyrics) };
};

const _parseContentInBlocks = (blocks) => {
	return blocks.map(b => {
		if (b.content) {
			b.content = b.content.map(line => {
				return _parseLine(line)
			});

			if (b.content.map(b => b.lyrics).join('').replace(/^\s+|\s+$/g, '') === "") {
				b.type = "only-chords"
			}
		}

		return b;
	})
}

const _transposeChords = (blocks, transposition) => {
	blocks = blocks.map(block => {
		if (block.content) {
			block.content = block.content.map(content => ({
				...content, chords: content.chords.map(chord => {
					const chordObject = Chord.parse(chord.chord);
					if (chordObject === null) return chord
					return {
						...chord,
						chord: chordTranspose.transpose(chordObject, transposition).toString()
					}
				})
			}))
		}
		return block;
	})
	return blocks
}

const _divideChordsInChordPro = (chordpro) => {
	chordpro = chordpro.replace(/]\[/g,'] [')
	chordpro = chordpro.replace(/\[([^\]]+)]/g,
		(match, chordString) => "[" + chordString.replace(/,/g, "] [") + "]"
	)
	return chordpro
}

const chordproJson = (chordpro, transposition = 0) => {
	chordpro = _divideChordsInChordPro(chordpro)
	let { parameters, blocks } = _parseBlocks(chordpro);
	blocks = _parseContentInBlocks(blocks);
	blocks = _transposeChords(blocks, transposition)

	return {
		parameters,
		blocks,
		containsHeader: !!blocks.find(
			b => ([ 'paragraph', 'verse' ].includes(b.type) && b.value) || [ 'chorus' ].includes(b.type)
		)
	};
}

export default {
	install(Vue) {
		Vue.$chordproJson = chordproJson
	}
};
